/**
 * LE SCEAU D'UNE CLÔTURE — ce que la Comptabilité confie au sceau du shell.
 *
 * Ce module ne fait AUCUNE écriture : il décrit le sceau (textes, page
 * d'arrivée, sélecteurs de la fiche de la nuit) et le demande au bus. Il est
 * appelé après la réponse 2xx d'une finalisation — ou, en aperçu, sur une
 * nuit DÉJÀ finalisée, sans rien envoyer au serveur.
 *
 * Le sceau ne montre aucun montant : le retenu, le total des composantes et
 * le chiffre d'affaires de la fiche sont trois nombres différents, et la
 * fiche de la nuit, juste derrière, montre les vrais.
 */

import type { ToastFn } from '../admin/primitives.js'
import { arriverSansSceau, jouerSceau, type Sceau } from '../shell/sceau-bus.js'
import { nuitLongue, type ClosingDetail } from './ClosingCommon.js'
import { visuelEnCache } from './ClosingHero.js'

/** Le toast de toujours, à l'issue d'une finalisation réelle. */
export const ANNONCE_FINALISEE = 'Nuit finalisée. Les composantes de recette sont produites.'
/** Le toast de l'aperçu : il dit qu'il n'a rien fait. */
export const ANNONCE_APERCU = 'Aperçu de la finalisation : rien n’a été écrit.'

export function sceauDeCloture(d: ClosingDetail, origine: DOMRect | null, apercu: boolean): Sceau {
  const maintenues = d.soirees.filter((s) => s.status === 'SCHEDULED')
  const premiere = maintenues[0] ?? null
  // Le visuel déjà connu de l'assistant : la fiche le monte d'emblée (cache
  // partagé), le sceau attend donc qu'il soit là avant de mesurer la pastille.
  const visuel = premiere ? visuelEnCache(premiere.id) : null
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d.business_date) ? d.business_date : null
  return {
    titre: 'Nuit finalisée',
    sousTitre: date
      ? [nuitLongue(date), ...maintenues.map((s) => s.name)].join(' · ')
      : maintenues.map((s) => s.name).join(' · '),
    puce: apercu ? 'Aperçu · rien n’a été écrit' : d.revision > 1 ? `Révision ${d.revision}` : null,
    // Une date illisible (ne doit pas arriver) mène au calendrier des nuits, jamais à une adresse inventée.
    destination: date ? `/comptabilite/nuits/${date}` : '/comptabilite/nuits',
    pret: '.fn-page .fwz-hero',
    attendre: visuel ? '.fn-page .fwz-hero--visuel' : null,
    ancre: '.fn-page .fwz-hero__etat .fn-etat--ok > svg',
    focus: '.fn-page .fwz-hero__titre',
    classeArrivee: 'ffin-arrivee',
    annonce: apercu ? ANNONCE_APERCU : ANNONCE_FINALISEE,
    origine,
    teinte: visuel?.dominant_hex ?? null,
  }
}

/** Joue le sceau ; sans lecteur (ou s'il est occupé), l'arrivée simple et le toast. */
export function sceller(d: ClosingDetail, origine: DOMRect | null, apercu: boolean, toast: ToastFn): void {
  const s = sceauDeCloture(d, origine, apercu)
  if (!jouerSceau(s)) {
    toast('ok', s.annonce)
    arriverSansSceau(s.destination, s.focus)
  }
}
